/**
 * Session Status Badge
 * Displays the current session status with color coding
 */

import type { CashRegisterSession, CashRegisterStatus } from '@/types/pos.types';
import { useTranslation } from '@kgc/i18n';

interface SessionStatusBadgeProps {
  session: CashRegisterSession | null;
  onClick?: () => void;
}

const statusClassNames: Record<CashRegisterStatus, { className: string; icon: string }> = {
  OPEN: {
    className:
      'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 border-green-300 dark:border-green-700',
    icon: '●',
  },
  SUSPENDED: {
    className:
      'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 border-yellow-300 dark:border-yellow-700',
    icon: '⏸',
  },
  CLOSED: {
    className:
      'bg-gray-100 dark:bg-[#1f5f6b] text-gray-700 dark:text-[#ccfbf1] border-gray-300 dark:border-[#2d7d8a]',
    icon: '○',
  },
};

const statusLabelKeys: Record<CashRegisterStatus, string> = {
  OPEN: 'sales.session.status.open',
  SUSPENDED: 'sales.session.status.suspended',
  CLOSED: 'sales.session.status.closed',
};

export function SessionStatusBadge({ session, onClick }: SessionStatusBadgeProps) {
  const { t } = useTranslation();

  if (!session) {
    return (
      <button
        onClick={onClick}
        className="flex items-center gap-2 rounded-lg border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/20 px-3 py-1.5 text-sm font-medium text-red-700 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30"
      >
        <span>○</span>
        <span>{t('sales.session.status.noSession')}</span>
      </button>
    );
  }

  const config = statusClassNames[session.status];
  const label = t(statusLabelKeys[session.status]);

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString('hu-HU', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 rounded-lg border px-3 py-1.5 text-sm font-medium hover:opacity-80 ${config.className}`}
    >
      <span>{config.icon}</span>
      <div className="flex flex-col items-start">
        <span>{label}</span>
        <span className="text-xs opacity-70">
          {session.sessionNumber} | {formatTime(session.openedAt)}
        </span>
      </div>
    </button>
  );
}
